import React, { useState, useRef, useEffect } from 'react';
import { Auth } from 'aws-amplify';
import styles from './AIGeneratedPreviewModal.module.css';
import MathParser from '../MathParser';
import 'mathlive';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { loadStripe } from '@stripe/stripe-js';
import katex from 'katex';
import 'katex/dist/katex.min.css';

if (typeof window !== 'undefined') {
  window.katex = katex;
}

// Helper to convert $latex$ to Quill HTML formula spans
function convertLatexToQuillHtml(text) {
  if (!text) return '';
  let html = '';
  let i = 0;
  while (i < text.length) {
    if (text[i] !== '$') {
      const char = text[i++];
      if (char === '<') html += '&lt;';
      else if (char === '>') html += '&gt;';
      else if (char === '&') html += '&amp;';
      else html += char;
      continue;
    }
    
    if (i + 1 < text.length && text[i + 1] === '$') {
      const closeIdx = text.indexOf('$$', i + 2);
      if (closeIdx !== -1) {
        const content = text.substring(i + 2, closeIdx);
        const escapedContent = content.replace(/"/g, '&quot;');
        html += `<span class="ql-formula" data-value="${escapedContent}"></span>`;
        i = closeIdx + 2;
        continue;
      }
    }

    let j = i + 1;
    while (j < text.length && text[j] !== '$') {
      if (text[j] === '\\') j++;
      j++;
    }
    if (j < text.length) {
      const content = text.substring(i + 1, j);
      const hasLatex = /[\\^_{}]/.test(content);
      let isMath = hasLatex;
      if (!hasLatex) {
        const validSpacing = !/^\s/.test(content) && !/\s$/.test(content);
        const noLongWords = !/[a-zA-Z]{3,}/.test(content);
        if (validSpacing && noLongWords && content.length > 0) isMath = true;
      }
      if (isMath) {
        const escapedContent = content.replace(/"/g, '&quot;');
        html += `<span class="ql-formula" data-value="${escapedContent}"></span>`;
        i = j + 1;
        continue;
      }
    }
    
    html += '$';
    i++;
  }
  return html;
}

function getRawTextFromQuill(editor) {
  if (!editor) return '';
  const delta = editor.getContents();
  let text = '';
  if (delta && delta.ops) {
    delta.ops.forEach(op => {
      if (typeof op.insert === 'string') {
        text += op.insert;
      } else if (op.insert && op.insert.formula) {
        text += `$${op.insert.formula}$`;
      }
    });
  }
  if (text.endsWith('\n')) {
    text = text.slice(0, -1);
  }
  return text;
}

const quillModules = {
  toolbar: false,
  formula: true,
};

function AIGeneratedPreviewModal({ isOpen, onClose, onApprove, category, subcategory, level, displayformat, jwtToken, targetCount }) {
  const [sampleQuestion, setSampleQuestion] = useState('');
  const [sampleQuestionHtml, setSampleQuestionHtml] = useState('');
  
  const [sampleAnswer, setSampleAnswer] = useState('');
  const [sampleAnswerHtml, setSampleAnswerHtml] = useState('');
  
  const [count, setCount] = useState(targetCount || 5);

  useEffect(() => {
    if (targetCount) {
      setCount(targetCount);
    }
  }, [targetCount]);

  const [temperature, setTemperature] = useState(0.7);
  const [isGenerating, setIsGenerating] = useState(false);
  const [generatedQuestions, setGeneratedQuestions] = useState([]);
  const [error, setError] = useState('');
  
  const [username, setUsername] = useState('');
  const [walletBalance, setWalletBalance] = useState(0.00);
  const [isFetchingBalance, setIsFetchingBalance] = useState(false);

  useEffect(() => {
    const fetchBalance = async () => {
      setIsFetchingBalance(true);
      try {
        const user = await Auth.currentAuthenticatedUser();
        const uname = user.username;
        setUsername(uname);

        const requestOptions = {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': jwtToken
          },
          body: JSON.stringify({ username: uname })
        };
        const response = await fetch('https://api.quizwizard.net/quiz/getuser', requestOptions);
        const data = await response.json();
        setWalletBalance(parseFloat(data.walletBalance || 0));
      } catch (err) {
        console.error("Failed to fetch wallet balance", err);
      } finally {
        setIsFetchingBalance(false);
      }
    };
    if (isOpen && jwtToken) {
      fetchBalance();
    }
  }, [isOpen, jwtToken]);

  const generateCost = count * 0.10;

  // Math Builder State
  const [showBuilder, setShowBuilder] = useState(false);
  const [activeInput, setActiveInput] = useState(null);
  const [mathBuilderValue, setMathBuilderValue] = useState('');
  
  const questionQuillRef = useRef(null);
  const answerQuillRef = useRef(null);
  const generatedQuillRefs = useRef({});
  const mathFieldRef = useRef(null);

  useEffect(() => {
    const handleMathInput = (e) => {
      setMathBuilderValue(e.target.value);
    };

    if (showBuilder && mathFieldRef.current) {
      mathFieldRef.current.addEventListener('input', handleMathInput);
      mathFieldRef.current.focus();
    }

    return () => {
      if (mathFieldRef.current) {
        mathFieldRef.current.removeEventListener('input', handleMathInput);
      }
    };
  }, [showBuilder]);

  if (!isOpen) return null;

  const toggleBuilder = (e, field) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    if (showBuilder && activeInput === field) {
      setShowBuilder(false);
      setActiveInput(null);
    } else {
      setShowBuilder(true);
      setActiveInput(field);
      setMathBuilderValue('');
    }
  };

  const insertMath = () => {
    let quillRef;
    if (activeInput === 'question') quillRef = questionQuillRef.current;
    else if (activeInput === 'answer') quillRef = answerQuillRef.current;
    else if (activeInput && activeInput.startsWith('gen_')) {
      quillRef = generatedQuillRefs.current[activeInput];
    }
    
    if (!quillRef) return;
    
    const editor = quillRef.getEditor();
    const range = editor.getSelection(true);
    const index = range ? range.index : editor.getLength();
    
    editor.insertEmbed(index, 'formula', mathBuilderValue);
    editor.setSelection(index + 1);
    
    setShowBuilder(false);
    setActiveInput(null);
  };

  const handleQuestionChange = (content, delta, source, editor) => {
    setSampleQuestionHtml(content);
    setSampleQuestion(getRawTextFromQuill(editor));
  };

  const handleAnswerChange = (content, delta, source, editor) => {
    setSampleAnswerHtml(content);
    setSampleAnswer(getRawTextFromQuill(editor));
  };

  const handleGenerate = async (e) => {
    e.preventDefault();
    if (!sampleQuestion.trim()) {
      setError('Please provide a sample question.');
      return;
    }

    setError('');
    setIsGenerating(true);
    setGeneratedQuestions([]);

    const augmentedSampleQuestion = sampleQuestion + 
      "\n\n(CRITICAL INSTRUCTIONS:\n" +
      "1. Deeply analyze the EXACT difficulty level, core mathematical concept, and structural pattern of this sample question.\n" +
      "2. You MUST generate questions that strictly match this exact same complexity and conceptual structure. For example, if the sample is simple arithmetic (e.g., 2+3), generate ONLY simple arithmetic questions. Do NOT arbitrarily escalate the difficulty or change the concept to advanced topics unless the sample explicitly contains them.\n" +
      "3. If the sample question involves a specific class of functions or operations, your generated questions MUST use that EXACT SAME class. Do not substitute or escalate it.\n" +
      "4. EVERY SINGLE generated question MUST have its own complete, independent question text in the 'question' field. NEVER leave the question text blank.\n" +
      "5. Formatting: You MUST wrap ALL math expressions, variables, numbers, inequalities, and formulas in single-dollars $...$ (e.g. write $x > -4$ instead of x > -4). Even simple expressions like $x=5$ must be wrapped.\n" +
      "6. This strict formatting requirement applies to the question text, ALL choices, AND the correct answer! If a choice is entirely a mathematical expression, the entire string must be wrapped in $...$.\n" +
      "7. Use double-dollars $$...$$ ONLY for standalone centered equations. NEVER put punctuation immediately after a math equation.\n" +
      "8. JSON ESCAPING: Because your output is JSON, you MUST double-escape ALL LaTeX backslashes! For example, write \\\\frac instead of \\frac, and \\\\theta instead of \\theta. Do NOT use single backslashes for LaTeX commands.)";

    const payload = {
      sampleQuestion: augmentedSampleQuestion,
      sampleAnswer,
      category: Array.isArray(category) ? category[0] : category,
      subcategory: Array.isArray(subcategory) ? subcategory[0] : subcategory,
      level: Array.isArray(level) ? level[0] : level,
      displayformat: displayformat || null,
      temperature,
      count,
      username
    };

    try {
      const response = await fetch('https://api.quizwizard.net/quiz/generatequestions', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': jwtToken
        },
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        if (response.status === 504) {
          throw new Error("AWS API Gateway Timeout (504): Generating this many questions took longer than the hard 29-second AWS limit. (OpenAI is likely still generating them in the background!) Please try generating fewer questions at a time (e.g., 5-10) or upgrade to a Lambda Function URL to bypass this limit.");
        }
        let errMessage = `Server returned error status: ${response.status}`;
        try {
          const errData = await response.json();
          if (errData.error) errMessage = errData.error;
        } catch(e) {}
        throw new Error(errMessage);
      }

      const startData = await response.json();
      if (startData.jobId) {
        if (startData.newBalance !== undefined) {
          setWalletBalance(startData.newBalance);
        }
        
        let jobComplete = false;
        while (!jobComplete) {
          await new Promise(resolve => setTimeout(resolve, 3000));
          
          const statusRes = await fetch('https://api.quizwizard.net/quiz/getaijobstatus', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'Authorization': jwtToken },
            body: JSON.stringify({ username, jobId: startData.jobId })
          });
          
          if (!statusRes.ok) throw new Error('Failed to check background job status.');
          
          const statusData = await statusRes.json();
          if (statusData.status === 'COMPLETED') {
            jobComplete = true;
            if (statusData.questions && Array.isArray(statusData.questions)) {
              
              const sanitizeAIString = (str) => {
                if (typeof str !== 'string') return str;
                return str.replace(/\\\//g, '/'); // Fix AI over-escaping forward slashes (e.g., 20 \/ 4 -> 20 / 4)
              };

              const processed = statusData.questions.map(q => {
                const safeQ = sanitizeAIString(q.question);
                const safeAns = sanitizeAIString(q.correctanswer);
                const safeChoices = q.answers ? q.answers.map(a => sanitizeAIString(a)) : undefined;

                return {
                  ...q,
                  question: safeQ,
                  correctanswer: safeAns,
                  answers: safeChoices,
                  questionHtml: convertLatexToQuillHtml(safeQ),
                  correctanswerHtml: convertLatexToQuillHtml(safeAns),
                  answersHtml: safeChoices ? safeChoices.map(a => convertLatexToQuillHtml(a)) : undefined
                };
              });
              setGeneratedQuestions(processed);
            } else {
              throw new Error('Received invalid data format from generation server.');
            }
          } else if (statusData.status === 'FAILED') {
            jobComplete = true;
            setWalletBalance(prev => prev + (count * 0.10)); // Refund the balance
            throw new Error(statusData.error || 'Background AI generation failed.');
          }
          // If PENDING, loop continues
        }
      } else {
        throw new Error('Server did not return a background job ID.');
      }
    } catch (err) {
      setError(err.message || 'Failed to generate questions. Please try again.');
    } finally {
      setIsGenerating(false);
    }
  };

  const handleTopUp = async (amount) => {
    setIsGenerating(true);
    setError('');
    try {
      const user = await Auth.currentAuthenticatedUser();
      const uname = user.username.includes("Google_") ? user.attributes.email : user.username;
      const email = user.attributes.email;
      
      const plan_type = `TOPUP_${amount}`;
      const url = "https://api.quizwizard.net/quiz/stripePriceId";
      const body = { plan_type, username: uname, email, subscriptionType: 'individual' };

      const response = await fetch(url, {
          method: "POST",
          headers: { "Content-Type": "application/json", Authorization: jwtToken },
          body: JSON.stringify(body),
      });

      if (!response.ok) throw new Error('Failed to initiate top-up. Please try again.');

      const data = await response.json();
      if (!data.sessionId) throw new Error("Failed to create Stripe session.");

      const stripePromiseIndividual = loadStripe(process.env.REACT_APP_STRIPE_PUBLIC_KEY || "pk_live_51ROTyHAsVF5kDdKhZlxgWcDgN3Ty5rp1WLF0QSnSML8twyvsxWo2qsa6KFpYi0BmC0Weo8asQjm6Py4BuLeEMgqz00fEtiOps0");
      const stripe = await stripePromiseIndividual;
      await stripe.redirectToCheckout({ sessionId: data.sessionId });
    } catch(err) {
      setError(err.message);
      setIsGenerating(false);
    }
  };

  const handleEditQuestionText = (index, content, editor) => {
    const updated = [...generatedQuestions];
    updated[index].questionHtml = content;
    if (editor) updated[index].question = getRawTextFromQuill(editor);
    setGeneratedQuestions(updated);
  };

  const handleEditCorrectAnswer = (index, content, editor) => {
    const updated = [...generatedQuestions];
    updated[index].correctanswerHtml = content;
    if (editor) updated[index].correctanswer = getRawTextFromQuill(editor);
    setGeneratedQuestions(updated);
  };

  const handleEditChoice = (qIndex, choiceIndex, content, editor) => {
    const updated = [...generatedQuestions];
    if (updated[qIndex].answersHtml) {
      updated[qIndex].answersHtml[choiceIndex] = content;
    }
    if (updated[qIndex].answers && editor) {
      updated[qIndex].answers[choiceIndex] = getRawTextFromQuill(editor);
    }
    setGeneratedQuestions(updated);
  };

  const handleDiscard = (index) => {
    const updated = generatedQuestions.filter((_, i) => i !== index);
    setGeneratedQuestions(updated);
  };

  const handleApproveAll = () => {
    if (generatedQuestions.length === 0) return;
    
    // Auto-wrap math symbols in $ signs before saving to the main question bank
    // in case the AI forgot to include them.
    const autoWrapMath = (str) => {
      if (!str) return str;
      if (str.includes('$')) return str; // If it already contains $ anywhere, trust the AI's formatting
      if (str.includes('\\') || str.includes('^') || str.includes('_')) {
        return `$${str}$`;
      }
      return str;
    };

    const formattedQuestions = generatedQuestions.map(q => {
      const formattedQ = { ...q };
      formattedQ.question = autoWrapMath(q.question);
      formattedQ.correctanswer = autoWrapMath(q.correctanswer);
      if (q.answers) {
        formattedQ.answers = q.answers.map(ans => autoWrapMath(ans));
      }
      return formattedQ;
    });

    onApprove(formattedQuestions);
    onClose();
  };

  const formatAnswer = (ans) => {
    if (!ans) return ans;
    const stripped = ans.replace(/^\$+|\$+$/g, '');
    if (stripped.includes('\\') || stripped.includes('^') || stripped.includes('_') || (ans.startsWith('$') && ans.endsWith('$'))) {
      return `$\\displaystyle ${stripped}$`;
    }
    return ans;
  };

  return (
    <div className={styles.pageContainer}>
      <div className={styles.pageContent}>
        <button className={styles.closeBtn} onClick={onClose}>&times;</button>
        
        <h2 className={styles.modalTitle}>✨ Generate Questions with AI</h2>
        <p className={styles.modalSubtitle}>Provide a sample question to generate similar level questions matching your topic.</p>

        {error && <div className={styles.errorMessage}>{error}</div>}

        {generatedQuestions.length === 0 && !isGenerating ? (
          <form onSubmit={handleGenerate} className={styles.form}>
            <style>{`
              .ai-quill-wrapper .quill {
                border: none !important;
              }
              .ai-quill-wrapper .ql-container.ql-snow {
                border: none !important;
                font-family: inherit;
                font-size: 1rem;
              }
              .ai-quill-wrapper .ql-editor {
                min-height: 80px;
              }
              .ai-quill-wrapper-answer .ql-editor {
                min-height: 50px;
              }
            `}</style>
            <div className={styles.formGroup} style={{ position: 'relative' }}>
              <label className={styles.label}>Sample Question</label>
              <div style={{ display: 'flex', alignItems: 'flex-start' }} className="ai-quill-wrapper">
                <div style={{ flex: 1, backgroundColor: 'rgba(10, 10, 12, 0.4)', borderRadius: '8px', border: '1px solid rgba(255, 255, 255, 0.08)' }}>
                  <ReactQuill
                    ref={questionQuillRef}
                    theme="snow"
                    value={sampleQuestionHtml}
                    onChange={handleQuestionChange}
                    modules={quillModules}
                    placeholder="e.g., If a triangle has sides of length 3 and 4, and the angle between them is 90 degrees, what is the hypotenuse?"
                    style={{ color: '#f1f1f5' }}
                  />
                </div>
                <button 
                  type="button"
                  onClick={(e) => toggleBuilder(e, 'question')}
                  title="Toggle Math Equation Builder"
                  style={{ 
                    marginLeft: '10px',
                    background: '#f9f9f9', 
                    border: '1px solid #ccc', 
                    borderRadius: '8px', 
                    padding: '8px 12px', 
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: '#333',
                    transition: 'all 0.2s ease'
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.background = '#f0f0f0'}
                  onMouseLeave={(e) => e.currentTarget.style.background = '#f9f9f9'}
                >
                  <span style={{ fontSize: '24px' }}>⌨️</span>
                </button>
              </div>
              {showBuilder && activeInput === 'question' && (
                <div style={{
                  backgroundColor: '#fff', border: '1px solid #ccc', borderRadius: '12px',
                  padding: '15px', width: '100%', marginTop: '12px'
                }}>
                  <div style={{ marginBottom: '10px', fontWeight: 'bold', color: '#333' }}>Math Equation Builder</div>
                  <math-field
                    ref={mathFieldRef}
                    style={{ width: '100%', fontSize: '1.4rem', padding: '12px', backgroundColor: '#f9f9f9', border: '1px solid #ccc', borderRadius: '8px', color: '#333', outline: 'none' }}
                  >
                    {mathBuilderValue}
                  </math-field>
                  <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '15px', gap: '12px' }}>
                    <button type="button" onClick={(e) => toggleBuilder(e, 'question')} style={{ padding: '10px 18px', borderRadius: '8px', border: '1px solid #ccc', background: 'transparent', color: '#555', cursor: 'pointer', fontWeight: '600' }}>Cancel</button>
                    <button type="button" onClick={insertMath} style={{ padding: '10px 18px', borderRadius: '8px', border: 'none', background: '#4caf50', color: '#fff', cursor: 'pointer', fontWeight: '600', boxShadow: '0 4px 10px rgba(76, 175, 80, 0.2)' }}>Insert Math</button>
                  </div>
                </div>
              )}
            </div>

            <div className={styles.formGroup} style={{ position: 'relative' }}>
              <label className={styles.label}>Sample Answer (Optional)</label>
              <div style={{ display: 'flex', alignItems: 'center' }} className="ai-quill-wrapper ai-quill-wrapper-answer">
                <div style={{ flex: 1, backgroundColor: 'rgba(10, 10, 12, 0.4)', borderRadius: '8px', border: '1px solid rgba(255, 255, 255, 0.08)' }}>
                  <ReactQuill
                    ref={answerQuillRef}
                    theme="snow"
                    value={sampleAnswerHtml}
                    onChange={handleAnswerChange}
                    modules={quillModules}
                    placeholder="e.g., 5"
                    style={{ color: '#f1f1f5' }}
                  />
                </div>
                <button 
                  type="button"
                  onClick={(e) => toggleBuilder(e, 'answer')}
                  title="Toggle Math Equation Builder"
                  style={{ 
                    marginLeft: '10px',
                    background: '#f9f9f9', 
                    border: '1px solid #ccc', 
                    borderRadius: '8px', 
                    padding: '8px 12px', 
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: '#333',
                    transition: 'all 0.2s ease'
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.background = '#f0f0f0'}
                  onMouseLeave={(e) => e.currentTarget.style.background = '#f9f9f9'}
                >
                  <span style={{ fontSize: '24px' }}>⌨️</span>
                </button>
              </div>
              {showBuilder && activeInput === 'answer' && (
                <div style={{
                  backgroundColor: '#fff', border: '1px solid #ccc', borderRadius: '12px',
                  padding: '15px', width: '100%', marginTop: '12px'
                }}>
                  <div style={{ marginBottom: '10px', fontWeight: 'bold', color: '#333' }}>Math Equation Builder</div>
                  <math-field
                    ref={mathFieldRef}
                    style={{ width: '100%', fontSize: '1.4rem', padding: '12px', backgroundColor: '#f9f9f9', border: '1px solid #ccc', borderRadius: '8px', color: '#333', outline: 'none' }}
                  >
                    {mathBuilderValue}
                  </math-field>
                  <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '15px', gap: '12px' }}>
                    <button type="button" onClick={(e) => toggleBuilder(e, 'answer')} style={{ padding: '10px 18px', borderRadius: '8px', border: '1px solid #ccc', background: 'transparent', color: '#555', cursor: 'pointer', fontWeight: '600' }}>Cancel</button>
                    <button type="button" onClick={insertMath} style={{ padding: '10px 18px', borderRadius: '8px', border: 'none', background: '#4caf50', color: '#fff', cursor: 'pointer', fontWeight: '600', boxShadow: '0 4px 10px rgba(76, 175, 80, 0.2)' }}>Insert Math</button>
                  </div>
                </div>
              )}
            </div>

            {/* Count slider removed as per user request to use pre-configured quiz count */}
            <div className={styles.formGroup}>
              <label className={styles.label}>Creativity Level (Temperature): {temperature.toFixed(1)}</label>
              <input
                type="range"
                min="0"
                max="1"
                step="0.1"
                value={temperature}
                onChange={(e) => setTemperature(parseFloat(e.target.value))}
                className={styles.rangeInput}
              />
              <p className={styles.helperText} style={{ fontSize: '12px', color: '#666', marginTop: '5px' }}>
                Lower values keep questions strictly to the sample topic. Higher values encourage a creative mixture of multiple topics within the category.
              </p>
            </div>

            <div style={{ marginTop: '20px', padding: '15px', backgroundColor: '#f9f9f9', borderRadius: '8px', border: '1px solid #e0e0e0' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                <span style={{ fontSize: '1.1rem', color: '#333', fontWeight: '500' }}>Coin Balance:</span>
                <span style={{ fontSize: '1.2rem', fontWeight: 'bold', color: walletBalance >= generateCost ? '#4caf50' : '#f44336' }}>
                  {Math.floor(walletBalance * 10)} Coins
                </span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ fontSize: '1.1rem', color: '#555' }}>Cost to Generate ({count} Qs):</span>
                <span style={{ fontSize: '1.1rem', color: '#333', fontWeight: '500' }}>{count} Coins</span>
              </div>
              
              {walletBalance < generateCost && (
                <div style={{ marginTop: '15px', paddingTop: '15px', borderTop: '1px solid #e0e0e0' }}>
                  <p style={{ color: '#e65100', marginBottom: '10px', fontSize: '0.9rem', fontWeight: '500' }}>
                    Insufficient balance. Please top up your coins to continue generating AI questions.
                  </p>
                  <div style={{ display: 'flex', gap: '10px' }}>
                    <button type="button" onClick={() => handleTopUp(5)} className={styles.backBtn} style={{ flex: 1 }}>Top Up 50 Coins ($5)</button>
                    <button type="button" onClick={() => handleTopUp(10)} className={styles.backBtn} style={{ flex: 1 }}>Top Up 100 Coins ($10)</button>
                  </div>
                </div>
              )}
            </div>

            <button type="submit" className={styles.generateBtn} disabled={walletBalance < generateCost || isFetchingBalance}>
              {walletBalance < generateCost ? 'Insufficient Coins' : 'Generate Questions'}
            </button>
          </form>
        ) : isGenerating ? (
          <div className={styles.loaderContainer}>
            <div className={styles.spinner}></div>
            <p className={styles.loadingText}>AI is thinking of similar questions...</p>
          </div>
        ) : (
          <div className={styles.previewContainer}>
            <h3 className={styles.previewHeading}>Review Generated Questions ({generatedQuestions.length})</h3>
            <div className={styles.questionsList}>
              {generatedQuestions.map((q, qIndex) => (
                <div key={qIndex} className={styles.questionCard}>
                  <div className={styles.cardHeader}>
                    <span className={styles.typeBadge}>
                      {q.displayformat === 'TextwithRadioButton' ? '🔘 Multiple Choice' : '📝 Text Input'}
                    </span>
                    <button className={styles.discardCardBtn} onClick={() => handleDiscard(qIndex)}>Discard</button>
                  </div>

                  <div className={styles.cardBody}>
                    <div className={styles.fieldGroup}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
                        <label className={styles.cardLabel} style={{ marginBottom: 0 }}>Question</label>
                        <button type="button" onClick={(e) => toggleBuilder(e, `gen_question_${qIndex}`)} title="Toggle Math Equation Builder" style={{ background: '#f9f9f9', border: '1px solid #ccc', borderRadius: '8px', padding: '4px 8px', cursor: 'pointer', fontSize: '16px' }}>⌨️</button>
                      </div>
                      {showBuilder && activeInput === `gen_question_${qIndex}` && (
                        <div style={{ backgroundColor: '#fff', border: '1px solid #ccc', borderRadius: '12px', padding: '15px', width: '100%', marginBottom: '12px' }}>
                          <div style={{ marginBottom: '10px', fontWeight: 'bold', color: '#333' }}>Math Equation Builder</div>
                          <math-field ref={mathFieldRef} style={{ width: '100%', fontSize: '1.4rem', padding: '12px', backgroundColor: '#f9f9f9', border: '1px solid #ccc', borderRadius: '8px', color: '#333', outline: 'none' }}>{mathBuilderValue}</math-field>
                          <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '15px', gap: '12px' }}>
                            <button type="button" onClick={(e) => toggleBuilder(e, `gen_question_${qIndex}`)} style={{ padding: '10px 18px', borderRadius: '8px', border: '1px solid #ccc', background: 'transparent', color: '#555', cursor: 'pointer', fontWeight: '600' }}>Cancel</button>
                            <button type="button" onClick={insertMath} style={{ padding: '10px 18px', borderRadius: '8px', border: 'none', background: '#4caf50', color: '#fff', cursor: 'pointer', fontWeight: '600', boxShadow: '0 4px 10px rgba(76, 175, 80, 0.2)' }}>Insert Math</button>
                          </div>
                        </div>
                      )}
                      <div className="ai-quill-wrapper" style={{ backgroundColor: 'rgba(10, 10, 12, 0.05)', borderRadius: '8px', border: '1px solid #ccc', marginBottom: '8px' }}>
                        <ReactQuill
                          ref={(el) => { generatedQuillRefs.current[`gen_question_${qIndex}`] = el; }}
                          theme="snow"
                          value={q.questionHtml || ''}
                          onChange={(content, delta, source, editor) => handleEditQuestionText(qIndex, content, editor)}
                          modules={quillModules}
                          style={{ color: '#333' }}
                        />
                      </div>
                    </div>

                    {q.displayformat === 'TextwithRadioButton' && q.answers && (
                      <div className={styles.choicesGrid}>
                        {q.answers.map((choice, cIndex) => (
                          <div key={cIndex} className={styles.choiceField}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
                              <label className={styles.choiceLabel} style={{ marginBottom: 0 }}>Choice {cIndex + 1}</label>
                              <button type="button" onClick={(e) => toggleBuilder(e, `gen_choice_${qIndex}_${cIndex}`)} title="Toggle Math Equation Builder" style={{ background: '#f9f9f9', border: '1px solid #ccc', borderRadius: '8px', padding: '2px 6px', cursor: 'pointer', fontSize: '14px' }}>⌨️</button>
                            </div>
                            {showBuilder && activeInput === `gen_choice_${qIndex}_${cIndex}` && (
                              <div style={{ backgroundColor: '#fff', border: '1px solid #ccc', borderRadius: '12px', padding: '15px', width: '100%', marginBottom: '12px' }}>
                                <div style={{ marginBottom: '10px', fontWeight: 'bold', color: '#333' }}>Math Equation Builder</div>
                                <math-field ref={mathFieldRef} style={{ width: '100%', fontSize: '1.4rem', padding: '12px', backgroundColor: '#f9f9f9', border: '1px solid #ccc', borderRadius: '8px', color: '#333', outline: 'none' }}>{mathBuilderValue}</math-field>
                                <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '15px', gap: '12px' }}>
                                  <button type="button" onClick={(e) => toggleBuilder(e, `gen_choice_${qIndex}_${cIndex}`)} style={{ padding: '10px 18px', borderRadius: '8px', border: '1px solid #ccc', background: 'transparent', color: '#555', cursor: 'pointer', fontWeight: '600' }}>Cancel</button>
                                  <button type="button" onClick={insertMath} style={{ padding: '10px 18px', borderRadius: '8px', border: 'none', background: '#4caf50', color: '#fff', cursor: 'pointer', fontWeight: '600', boxShadow: '0 4px 10px rgba(76, 175, 80, 0.2)' }}>Insert Math</button>
                                </div>
                              </div>
                            )}
                            <div className="ai-quill-wrapper ai-quill-wrapper-answer" style={{ backgroundColor: 'rgba(10, 10, 12, 0.05)', borderRadius: '8px', border: '1px solid #ccc', marginBottom: '8px' }}>
                              <ReactQuill
                                ref={(el) => { generatedQuillRefs.current[`gen_choice_${qIndex}_${cIndex}`] = el; }}
                                theme="snow"
                                value={q.answersHtml ? q.answersHtml[cIndex] : ''}
                                onChange={(content, delta, source, editor) => handleEditChoice(qIndex, cIndex, content, editor)}
                                modules={quillModules}
                                style={{ color: '#333' }}
                              />
                            </div>
                          </div>
                        ))}
                      </div>
                    )}

                    <div className={styles.fieldGroup}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
                        <label className={styles.cardLabel} style={{ marginBottom: 0 }}>Correct Answer</label>
                        <button type="button" onClick={(e) => toggleBuilder(e, `gen_answer_${qIndex}`)} title="Toggle Math Equation Builder" style={{ background: '#f9f9f9', border: '1px solid #ccc', borderRadius: '8px', padding: '4px 8px', cursor: 'pointer', fontSize: '16px' }}>⌨️</button>
                      </div>
                      {showBuilder && activeInput === `gen_answer_${qIndex}` && (
                        <div style={{ backgroundColor: '#fff', border: '1px solid #ccc', borderRadius: '12px', padding: '15px', width: '100%', marginBottom: '12px' }}>
                          <div style={{ marginBottom: '10px', fontWeight: 'bold', color: '#333' }}>Math Equation Builder</div>
                          <math-field ref={mathFieldRef} style={{ width: '100%', fontSize: '1.4rem', padding: '12px', backgroundColor: '#f9f9f9', border: '1px solid #ccc', borderRadius: '8px', color: '#333', outline: 'none' }}>{mathBuilderValue}</math-field>
                          <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '15px', gap: '12px' }}>
                            <button type="button" onClick={(e) => toggleBuilder(e, `gen_answer_${qIndex}`)} style={{ padding: '10px 18px', borderRadius: '8px', border: '1px solid #ccc', background: 'transparent', color: '#555', cursor: 'pointer', fontWeight: '600' }}>Cancel</button>
                            <button type="button" onClick={insertMath} style={{ padding: '10px 18px', borderRadius: '8px', border: 'none', background: '#4caf50', color: '#fff', cursor: 'pointer', fontWeight: '600', boxShadow: '0 4px 10px rgba(76, 175, 80, 0.2)' }}>Insert Math</button>
                          </div>
                        </div>
                      )}
                      <div className="ai-quill-wrapper ai-quill-wrapper-answer" style={{ backgroundColor: 'rgba(10, 10, 12, 0.05)', borderRadius: '8px', border: '1px solid #ccc', marginBottom: '8px' }}>
                        <ReactQuill
                          ref={(el) => { generatedQuillRefs.current[`gen_answer_${qIndex}`] = el; }}
                          theme="snow"
                          value={q.correctanswerHtml || ''}
                          onChange={(content, delta, source, editor) => handleEditCorrectAnswer(qIndex, content, editor)}
                          modules={quillModules}
                          style={{ color: '#333' }}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className={styles.actionRow}>
              <button className={styles.backBtn} onClick={() => setGeneratedQuestions([])}>
                Start Over
              </button>
              <button className={styles.approveBtn} onClick={handleApproveAll}>
                Approve & Add All
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default AIGeneratedPreviewModal;
